import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/consumption-register`;

// Every refusal from the register carries plain words AND the action that
// resolves it (see consumption_register_service.ConsumptionRegisterError),
// so the UI can offer a one-tap path instead of a bare error.
export const registerError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return { message: detail, action: null, detail: {} };
  if (detail?.message) return { message: detail.message, action: detail.action || null, detail };
  return { message: err?.message || "Something went wrong", action: null, detail: {} };
};

export const api = {
  masters: () => axios.get(`${API}/masters`).then((r) => r.data),
  sites: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/known-sites`).then((r) => r.data.sites),

  createDepartment: (body) => axios.post(`${API}/departments`, body).then((r) => r.data),
  people: () => axios.get(`${API}/people`).then((r) => r.data.people),
  departmentStock: (id) => axios.get(`${API}/departments/${id}/stock`).then((r) => r.data),
  updateDepartment: (id, body) => axios.put(`${API}/departments/${id}`, body).then((r) => r.data),
  costCenters: (siteId, refresh) => axios.get(`${API}/cost-centers`, { params: { site_id: siteId || undefined, refresh: refresh || undefined } }).then((r) => r.data),
  warehouses: (siteId) => axios.get(`${API}/warehouses`, { params: { site_id: siteId } }).then((r) => r.data.warehouses),
  createProcess: (body) => axios.post(`${API}/processes`, body).then((r) => r.data),
  updateProcess: (id, body) => axios.put(`${API}/processes/${id}`, body).then((r) => r.data),
  searchProducts: (q) => axios.get(`${API}/products/search`, { params: { q, limit: 8 } }).then((r) => r.data),
  mapItem: (body) => axios.post(`${API}/items`, body).then((r) => r.data),
  unmapItem: (id) => axios.delete(`${API}/items/${id}`).then((r) => r.data),

  register: (processId, date) => axios.get(`${API}/register`, { params: { process_id: processId, date } }).then((r) => r.data),
  openingBalances: (processId, balances) => axios.post(`${API}/opening-balances`, { process_id: processId, balances }).then((r) => r.data),
  matchOpening: (processId) => axios.post(`${API}/opening-balances/match`, { process_id: processId }).then((r) => r.data),
  saveEntry: (body) => axios.post(`${API}/entries`, body).then((r) => r.data),
  clearEntry: (body) => axios.post(`${API}/entries/delete`, body).then((r) => r.data),

  arrivals: (processId, live) => axios.get(`${API}/arrivals`, { params: { process_id: processId, live } }).then((r) => r.data),
  claimHandover: (body) => axios.post(`${API}/handovers`, body).then((r) => r.data),

  countSheet: (processId) => axios.get(`${API}/count`, { params: { process_id: processId } }).then((r) => r.data),
  submitCount: (processId, counts) => axios.post(`${API}/count`, { process_id: processId, counts }).then((r) => r.data),

  weeks: (processId) => axios.get(`${API}/weeks`, { params: { process_id: processId } }).then((r) => r.data.weeks),
  review: (processId, weekId) => axios.get(`${API}/weeks/review`, { params: { process_id: processId, week_id: weekId } }).then((r) => r.data),
  exportUrl: (processId, weekId) => `${API}/weeks/export?process_id=${encodeURIComponent(processId)}${weekId ? `&week_id=${encodeURIComponent(weekId)}` : ""}`,

  // Consumption Audit (admin only)
  auditOptions: () => axios.get(`${API}/audit/options`).then((r) => r.data),
  auditDashboard: (params) => axios.get(`${API}/audit/dashboard`, { params }).then((r) => r.data),
  auditExportUrl: (kind, params) =>
    `${API}/audit/export/${kind}?${new URLSearchParams(
      Object.entries(params).filter(([, v]) => v).map(([k, v]) => [k, String(v)]),
    ).toString()}`,

  // Phase 2 - one SAP consumption document per department per day, posted
  // by hand by the supervisor and dated the day the material was used.
  postingPreview: (departmentId, entryDate) =>
    axios.get(`${API}/postings/preview`, { params: { department_id: departmentId, entry_date: entryDate } }).then((r) => r.data),
  postDay: (departmentId, entryDate) =>
    axios.post(`${API}/postings`, { department_id: departmentId, entry_date: entryDate }).then((r) => r.data),
  posting: (id) => axios.get(`${API}/postings/${id}`).then((r) => r.data),
  postings: (params) => axios.get(`${API}/postings`, { params }).then((r) => r.data.postings),
  correctionPreview: (departmentId, entryDate, figures) =>
    axios.get(`${API}/postings/correction/preview`, {
      params: { department_id: departmentId, entry_date: entryDate, figures },
    }).then((r) => r.data),
  postCorrection: (body) => axios.post(`${API}/postings/correction`, body).then((r) => r.data),
  resolveUnknown: (id, body) => axios.post(`${API}/postings/${id}/resolve-unknown`, body).then((r) => r.data),

  // Jun 2026 - material standing in the department's godown staged into the
  // site's HOLD godown; anyone asks, the head's approval posts it to SAP.
  stoStock: (departmentId) => axios.get(`${API}/sto-requests/stock`, { params: { department_id: departmentId } }).then((r) => r.data),
  stoRequests: (departmentId) =>
    axios.get(`${API}/sto-requests`, { params: { department_id: departmentId } }).then((r) => r.data.requests),
  createStoRequest: (body) => axios.post(`${API}/sto-requests`, body).then((r) => r.data),
  approveStoRequest: (id) => axios.post(`${API}/sto-requests/${id}/approve`).then((r) => r.data),
  rejectStoRequest: (id, reason) => axios.post(`${API}/sto-requests/${id}/reject`, { reason }).then((r) => r.data),
};

export const qty = (value) =>
  value == null ? "—" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
